/**
 * Every TanStack Query key in the dashboard, in one place.
 *
 * Keys are never written as inline literals at call sites. The reason is concrete:
 * the API already has this bug server-side — the menu-tree Redis key is built by
 * `menuTreeKey()` in menus.service.ts but re-typed as a bare string literal in
 * orders.repository.ts, so one cache key now has two independent sources of truth
 * and a change to either silently stops the other from invalidating.
 *
 * A read and the writes that invalidate it MUST agree on the key. Importing it from
 * here is what makes that mechanical instead of a matter of remembering.
 *
 * Shape follows apps/guest: hierarchical arrays, most-general segment first, so a
 * `['payment-integrations']` prefix would invalidate every venue's entry at once.
 *
 * Only keys that are actually used live here. Speculative entries (`venues.*`, a
 * `.all` prefix per domain, `auth.me`) were removed rather than parked: `/api/venues`
 * and `/api/auth/me` are deliberately NOT cached — see CLAUDE.md §9 — and a key sitting
 * here for an uncached endpoint reads as though the migration happened.
 */
export const qk = {
  analytics: {
    /**
     * GET /api/venues/:slug/analytics/summary — above-the-fold dashboard group.
     * from/to are minute-quantized ISO instants (useVenueAnalytics does the
     * quantizing) so the key is stable across re-renders within a minute.
     */
    summary: (slug: string, from: string, to: string) =>
      ['analytics-summary', slug, from, to] as const,
    /** GET /api/venues/:slug/analytics/items — item-level dashboard group. */
    items: (slug: string, from: string, to: string) =>
      ['analytics-items', slug, from, to] as const,
    /** GET /api/venues/:slug/analytics/zero-sellers — full never-ordered list (modal). */
    zeroSellers: (slug: string, from: string, to: string) =>
      ['analytics-zero-sellers', slug, from, to] as const,
  },
  billing: {
    /** GET /api/billing/status — plan, addons, seat counts, AI credit usage. Org-scoped. */
    status: ['billing', 'status'] as const,
  },
  accounting: {
    /** GET /api/accounting/config — tax config. Org-scoped, not venue-scoped. */
    config: ['accounting', 'config'] as const,
  },
  paymentIntegrations: {
    /** GET /api/venues/:slug/payment-integrations — Ziina/Stripe connection status. */
    byVenue: (slug: string) => ['payment-integrations', slug] as const,
  },
  staff: {
    /** GET /api/venues/:slug/staff — venue staff list + org-wide count. */
    byVenue: (slug: string) => ['staff', slug] as const,
  },
} as const;
